import { Component, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxCurrencyDirective } from 'ngx-currency';
import { PercentualProgressBarComponent } from '../../../../shared/components/percentual-progress-bar/percentual-progress-bar.component';
import { MatSelectModule } from '@angular/material/select';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { RateioCentroCustoService } from '../../../../shared/services/apis/financas/rateio-centro-custo.service';
import { CentroCustoService } from '../../../../shared/services/apis/financas/centro-custo.service';
import { MaskService } from '../../../../shared/services/mask.service';
import {
  RateioCentroCustoRequest,
  RateioCentroCustoItemRequest,
} from '../../../../shared/services/requests/financas/rateio-centro-custo.request';
import { CentroCusto } from '../../../../shared/services/models/financas/centro-custo.model';
import { HeaderComponent } from '../../../../shared/components/header/header.component';

@Component({
  selector: 'app-rateio-centro-custo-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    NgxCurrencyDirective,
    LoadingBoxComponent,
    FooterActionComponent,
    HeaderComponent,
    PercentualProgressBarComponent,
  ],
  templateUrl: './rateio-centro-custo-cadastro.component.html',
  styleUrl: './rateio-centro-custo-cadastro.component.css',
})
export class RateioCentroCustoCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;

  public form: FormGroup;
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  public isEdicao: boolean = false;
  public idRateioCentroCusto: number = 0;

  public centrosCustoAtivos: CentroCusto[] = [];
  public somaPercentual: number = 0;
  public percentMask: any;

  constructor(
    private formBuilder: FormBuilder,
    private rateioCentroCustoService: RateioCentroCustoService,
    private centroCustoService: CentroCustoService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute,
    private maskService: MaskService
  ) {
    this.percentMask = this.maskService.getPercentMask();
  }

  ngOnInit(): void {
    this.createForm();
    this.carregarCentrosCustoAtivos();
    this.route.params.subscribe(params => {
      if (params['idRateioCentroCusto']) {
        this.idRateioCentroCusto = +params['idRateioCentroCusto'];
        this.isEdicao = true;
        this.carregarRateio();
      }
    });
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      nome: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(500)]],
      descricao: ['', [Validators.maxLength(2000)]],
      itens: this.formBuilder.array([]),
    });
  }

  get itensFormArray(): FormArray {
    return this.form.get('itens') as FormArray;
  }

  private carregarCentrosCustoAtivos(): void {
    // Carrega todos os centros de custo ativos (paginação grande para garantir todos)
    this.centroCustoService.getAll('', 0, 1000).subscribe({
      next: res => {
        if (!res.isError) {
          this.centrosCustoAtivos = res.listaCentrosCusto.filter(cc => cc.isAtivo);
        }
      },
      error: () => {},
    });
  }

  private carregarRateio(): void {
    this.isLoading = true;
    this.rateioCentroCustoService.getById(this.idRateioCentroCusto).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription || 'Erro ao carregar rateio.', AlertType.warning);
          this.voltar();
          return;
        }
        if (res.rateio) {
          this.form.patchValue({
            nome: res.rateio.nome,
            descricao: res.rateio.descricao || '',
          });
          // Preencher itens
          if (res.rateio.itens && res.rateio.itens.length > 0) {
            res.rateio.itens.forEach(item => {
              this.adicionarLinha(item.idCentroCusto, item.percentual);
            });
          }
          this.calcularSoma();
        }
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao carregar o rateio.', AlertType.error);
        this.voltar();
      },
    });
  }

  public adicionarLinha(idCentroCusto: number = 0, percentual: number = 0): void {
    const grupo = this.formBuilder.group({
      idCentroCusto: [idCentroCusto, [Validators.required]],
      percentual: [percentual, [Validators.required, Validators.min(0), Validators.max(100)]],
    });

    // Escutar mudanças no percentual para recalcular soma
    grupo.get('percentual')?.valueChanges.subscribe(() => this.calcularSoma());

    this.itensFormArray.push(grupo);
    this.calcularSoma();
  }

  public removerLinha(index: number): void {
    this.itensFormArray.removeAt(index);
    this.calcularSoma();
  }

  public calcularSoma(): void {
    this.somaPercentual = this.itensFormArray.controls.reduce((soma, ctrl) => {
      const val = parseFloat(ctrl.get('percentual')?.value) || 0;
      return soma + val;
    }, 0);
  }

  public get somaValida(): boolean {
    return Math.abs(this.somaPercentual - 100) <= 0.01;
  }

  /// Verifica se o centro de custo já está sendo usado em outra linha
  public centroCustoJaUtilizado(idCentroCusto: number, indexAtual: number): boolean {
    return this.itensFormArray.controls.some(
      (ctrl, i) => i !== indexAtual && ctrl.get('idCentroCusto')?.value === idCentroCusto
    );
  }

  /// Filtra os centros de custo disponíveis para uma linha (exclui os já selecionados em outras linhas)
  public centrosCustoDisponiveis(indexAtual: number): CentroCusto[] {
    const idsUsados = this.itensFormArray.controls
      .filter((_, i) => i !== indexAtual)
      .map(ctrl => ctrl.get('idCentroCusto')?.value)
      .filter(id => id > 0);

    return this.centrosCustoAtivos.filter(cc => !idsUsados.includes(cc.idCentroCusto));
  }

  public salvar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertService.show('Atenção', 'Preencha todos os campos obrigatórios.', AlertType.warning);
      return;
    }

    if (this.itensFormArray.length === 0) {
      this.alertService.show(
        'Atenção',
        'Adicione pelo menos um centro de custo ao rateio.',
        AlertType.warning
      );
      return;
    }

    if (!this.somaValida) {
      this.alertService.show(
        'Atenção',
        'A soma dos percentuais deve ser exatamente 100%.',
        AlertType.warning
      );
      return;
    }

    this.isSaving = true;
    const itens: RateioCentroCustoItemRequest[] = this.itensFormArray.controls.map(ctrl => ({
      idCentroCusto: ctrl.get('idCentroCusto')?.value,
      percentual: parseFloat(ctrl.get('percentual')?.value) || 0,
    }));

    const request: RateioCentroCustoRequest = {
      nome: this.form.get('nome')?.value?.trim(),
      descricao: this.form.get('descricao')?.value?.trim() || '',
      itens,
    };

    if (this.isEdicao) {
      this.rateioCentroCustoService.update(this.idRateioCentroCusto, request).subscribe({
        next: res => this.tratarRetorno(res, 'Configuração de rateio atualizada!'),
        error: err => this.tratarErro(err),
      });
    } else {
      this.rateioCentroCustoService.create(request).subscribe({
        next: res => this.tratarRetorno(res, 'Configuração de rateio cadastrada com sucesso!'),
        error: err => this.tratarErro(err),
      });
    }
  }

  private tratarRetorno(res: any, successMsg: string): void {
    this.isSaving = false;
    if (res.isError) {
      this.alertService.show('Atenção', res.errorDescription || 'Erro ao salvar rateio.', AlertType.warning);
    } else {
      this.alertService.show('Sucesso', successMsg, AlertType.success);
      this.voltar();
    }
  }

  private tratarErro(err: any): void {
    this.isSaving = false;
    this.alertService.show('Erro', 'Ocorreu um erro ao salvar.', AlertType.error);
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/financas/rateios-centro-custo']);
  }
}
